import { type VariantProps, cva } from "class-variance-authority";
import * as React from "react";
import { Typography, type TypographyVariant } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";

/**
 * Shared text stack for page, section, card, and compact headings.
 *
 * Variants:
 *   - `page` — page-level h1 text stack, without page chrome/actions.
 *   - `section` — section-level h2/h3 text stack.
 *   - `card` — card/subsection heading with compact description.
 *   - `compact` — dense label/title stack for table/card internals.
 */
const headingGroupVariants = cva("cw-v2-heading-group", {
  variants: {
    variant: {
      page: "cw-v2-heading-group--page",
      section: "cw-v2-heading-group--section",
      card: "cw-v2-heading-group--card",
      compact: "cw-v2-heading-group--compact",
    },
  },
  defaultVariants: {
    variant: "section",
  },
});

export type HeadingGroupHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type HeadingGroupVariant = NonNullable<VariantProps<typeof headingGroupVariants>["variant"]>;

const defaultHeadingLevelByVariant: Record<HeadingGroupVariant, HeadingGroupHeadingLevel> = {
  page: 1,
  section: 2,
  card: 3,
  compact: 3,
};

const titleVariantByVariant: Record<HeadingGroupVariant, TypographyVariant> = {
  page: "heading-large",
  section: "heading-large",
  card: "heading-small",
  compact: "body-large",
};

const descriptionVariantByVariant: Record<HeadingGroupVariant, TypographyVariant> = {
  page: "body-large",
  section: "body",
  card: "body",
  compact: "body-mono-small",
};

const headingElementByLevel: Record<HeadingGroupHeadingLevel, keyof React.JSX.IntrinsicElements> = {
  1: "h1",
  2: "h2",
  3: "h3",
  4: "h4",
  5: "h5",
  6: "h6",
};

export interface HeadingGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title">,
    VariantProps<typeof headingGroupVariants> {
  /** Optional small label above the title. */
  eyebrow?: React.ReactNode;
  /** Main heading text. Omit only for description-only helper stacks. */
  title?: React.ReactNode;
  /** Optional status/metadata rendered next to the title. */
  meta?: React.ReactNode;
  /** Supporting copy under the title row. */
  description?: React.ReactNode;
  /** Semantic heading level. Defaults by variant. */
  headingLevel?: HeadingGroupHeadingLevel;
  /** Override the title's type scale (defaults by variant). */
  titleVariant?: TypographyVariant;
  /** Override the description's type scale (defaults by variant). */
  descriptionVariant?: TypographyVariant;
}

export const HeadingGroup = React.forwardRef<HTMLDivElement, HeadingGroupProps>(
  function HeadingGroup(
    {
      children,
      className,
      description,
      descriptionVariant,
      eyebrow,
      headingLevel,
      meta,
      title,
      titleVariant,
      variant,
      ...props
    },
    ref,
  ) {
    const resolvedVariant = variant ?? "section";
    const resolvedHeadingLevel = headingLevel ?? defaultHeadingLevelByVariant[resolvedVariant];
    const titleAs = headingElementByLevel[resolvedHeadingLevel];
    const resolvedTitleVariant = titleVariant ?? titleVariantByVariant[resolvedVariant];
    const resolvedDescriptionVariant =
      descriptionVariant ?? descriptionVariantByVariant[resolvedVariant];

    return (
      <div
        ref={ref}
        className={cx(headingGroupVariants({ variant: resolvedVariant }), className)}
        {...props}
      >
        {eyebrow ? (
          <Typography as="div" className="cw-v2-heading-group__eyebrow" variant="overline">
            {eyebrow}
          </Typography>
        ) : null}
        {title || meta ? (
          <div className="cw-v2-heading-group__title-row">
            {title ? (
              typeof title === "string" || typeof title === "number" ? (
                <Typography
                  as={titleAs}
                  className="cw-v2-heading-group__title"
                  variant={resolvedTitleVariant}
                >
                  {title}
                </Typography>
              ) : (
                <div className="cw-v2-heading-group__title">{title}</div>
              )
            ) : null}
            {meta ? <div className="cw-v2-heading-group__meta">{meta}</div> : null}
          </div>
        ) : null}
        {description ? (
          typeof description === "string" || typeof description === "number" ? (
            <Typography
              as="div"
              className="cw-v2-heading-group__description"
              muted
              variant={resolvedDescriptionVariant}
            >
              {description}
            </Typography>
          ) : (
            <div className="cw-v2-heading-group__description">{description}</div>
          )
        ) : null}
        {children}
      </div>
    );
  },
);
HeadingGroup.displayName = "HeadingGroup";
